import useCatalogLabels from '@/hooks/PDP/useCatalogLabels';
import { DateTime } from 'luxon';

export const useDateHelpers = () => {
  const { twelveHourTimezones } = useCatalogLabels();

  const isTwelveHourTimezone = (tzAbbreviation: string): boolean =>
    twelveHourTimezones.includes(tzAbbreviation);

  const getFormatedDateRange = (timeZone: string, startDate: string, endDate?: string) => {
    const startDateTime = DateTime.fromISO(startDate, { zone: 'UTC' }).setZone(timeZone);
    const endDateTime = endDate
      ? DateTime.fromISO(endDate, { zone: 'UTC' }).setZone(timeZone).setLocale('en')
      : undefined;

    // Initialize the formatted date part
    let formattedDate;

    // Check if endDateTime is defined and if it is the same day as startDateTime
    if (endDateTime && startDateTime.hasSame(endDateTime, 'day')) {
      // If the end date is the same day, format only the start date
      formattedDate = startDateTime.toFormat('LLL dd');
    } else if (endDateTime) {
      // If the end date is different, format both dates
      formattedDate = `${startDateTime.toFormat('LLL dd')} – ${endDateTime.toFormat('dd')}`;
    } else {
      // If there is no end date, format only the start date
      formattedDate = startDateTime.toFormat('LLL dd');
    }

    const tzAbbreviation = getTimezoneAbbreviation(startDateTime);
    const useTwelveHourFormat = isTwelveHourTimezone(tzAbbreviation);

    // Use military time (24-hour) for non-US timezones, 12-hour for US
    const formattedTime = useTwelveHourFormat
      ? startDateTime.toFormat('h:mm a')
      : startDateTime.toFormat('H:mm');

    const timeZoneAbbreviation = useTwelveHourFormat
      ? startDateTime.toFormat('ZZZZ')
      : tzAbbreviation;

    // Construct the final output with conditional inclusion of end date
    return `${formattedDate}, ${formattedTime} ${timeZoneAbbreviation}`;
  };

  const getFormattedStartDateWithSessions = (
    timeZone: string,
    startDate: string,
    endDate?: string,
    customDuration?: string,
    sessions?: number | null,
    withTime?: boolean
  ) => {
    const startDateTime = DateTime.fromISO(startDate, { zone: 'UTC' }).setZone(timeZone);
    const endDateTime = endDate
      ? DateTime.fromISO(endDate, { zone: 'UTC' }).setZone(timeZone)
      : undefined;

    // Build formatted date
    const formattedDate =
      customDuration && customDuration?.length > 0
        ? startDateTime.toFormat('LLL dd') // force single date
        : endDateTime && !startDateTime.hasSame(endDateTime, 'day')
          ? `${startDateTime.toFormat('LLL dd')} – ${endDateTime.toFormat('dd')}`
          : startDateTime.toFormat('LLL dd');

    // If sessions override exists, use the new format
    if (sessions && sessions > 0) {
      let response = `Start: ${formattedDate} (${sessions} sessions)`;
      if (withTime) response = `${response} | ${getFormattedTimeWithTimezone(startDateTime)}`;
      return response;
    }

    // Otherwise, keep the old behavior (including time/timezone)
    return getFormatedDateRange(timeZone, startDate, endDate);
  };

  const getFormatedDateRangeParts = (timeZone: string, startDate: string, endDate?: string) => {
    const startDateTime = DateTime.fromISO(startDate, { zone: 'UTC' }).setZone(timeZone);
    const endDateTime = endDate
      ? DateTime.fromISO(endDate, { zone: 'UTC' }).setZone(timeZone)
      : undefined;

    // Initialize the formatted date part
    let formattedDate;
    let currentDay;

    // Check if endDateTime is defined and if it is the same day as startDateTime
    if (endDateTime && startDateTime.hasSame(endDateTime, 'day')) {
      // If the end date is the same day, format only the start date
      formattedDate = startDateTime.toFormat('LLL dd, yyyy');
      currentDay = undefined;
    } else if (endDateTime) {
      // If the end date is different, format both dates
      // Add year verifying if it's the same year or not
      if (startDateTime.year !== endDateTime.year) {
        formattedDate = `${startDateTime.toFormat('LLL dd, yyyy')} – ${endDateTime.toFormat('LLL dd, yyyy')}`;
      } else {
        formattedDate = `${startDateTime.toFormat('LLL dd')} – ${endDateTime.toFormat('dd, yyyy')}`;
      }
      const today = DateTime.local().setZone(timeZone).startOf('day');
      const startDateDiffDays = today.diff(startDateTime.startOf('day'), 'days').days;
      const endDateDiffDays = today.diff(endDateTime.startOf('day'), 'days').days;
      const isTodayWithinRange = startDateDiffDays >= 0 && endDateDiffDays <= 0;
      currentDay = isTodayWithinRange ? `Day ${startDateDiffDays + 1}` : undefined;
    } else {
      // If there is no end date, format only the start date
      formattedDate = startDateTime.toFormat('LLL dd, yyyy');
      currentDay = undefined;
    }
    // Construct the final output with conditional inclusion of end date
    return {
      date: formattedDate,
      time: getFormattedTimeWithTimezone(startDateTime),
      currentDay
    };
  };

  const getFormattedTimeWithTimezone = (dateTime: DateTime): string => {
    const tzAbbreviation = getTimezoneAbbreviation(dateTime);
    const useTwelveHourFormat = isTwelveHourTimezone(tzAbbreviation);

    // Use military time (24-hour) for non-US timezones, 12-hour for US
    const formattedTime = useTwelveHourFormat
      ? dateTime.toFormat('h:mm a')
      : dateTime.toFormat('H:mm');

    const timeZoneAbbreviation = useTwelveHourFormat ? dateTime.toFormat('ZZZZ') : tzAbbreviation;

    return `${formattedTime} ${timeZoneAbbreviation}`;
  };

  const getTime = (time: string, timeZone: string) => {
    const dateTime = DateTime.fromISO(time, { zone: 'UTC' }).setZone(timeZone);
    const tzAbbreviation = getTimezoneAbbreviation(dateTime);
    const useTwelveHour = isTwelveHourTimezone(tzAbbreviation);

    // Use military time (24-hour) for non-US timezones, 12-hour for US
    return useTwelveHour ? dateTime.toFormat('h:mm a') : dateTime.toFormat('H:mm');
  };

  return {
    getFormatedDateRange,
    getFormatedDateRangeParts,
    getFormattedStartDateWithSessions,
    getTime
  };
};

export const getTimezoneAbbreviation = (dateTime: DateTime): string => {
  const jsDate = dateTime.toJSDate();

  // Try to use toLocaleTimeString which sometimes gives better abbreviations
  try {
    const timeString = jsDate.toLocaleTimeString('en-US', {
      timeZone: dateTime.zoneName || undefined,
      timeZoneName: 'short'
    });

    const parts = timeString.split(' ');
    const potentialTz = parts[parts.length - 1];

    if (
      potentialTz &&
      potentialTz.length <= 5 &&
      !potentialTz.includes('+') &&
      !potentialTz.includes('-') &&
      !potentialTz.includes('GMT') &&
      potentialTz !== 'AM' &&
      potentialTz !== 'PM'
    ) {
      return potentialTz;
    }
  } catch (error) {
    console.warn('Error using toLocaleTimeString:', error);
  }

  // Try multiple Intl.DateTimeFormat configurations with different locales
  const localeConfigs = [
    { locale: 'en-SG', timeZoneName: 'short' as const }, // Singapore (SGT)
    { locale: 'en-HK', timeZoneName: 'short' as const }, // Hong Kong (HKT)
    { locale: 'en-GB', timeZoneName: 'short' as const }, // British (GMT)
    { locale: 'en-US', timeZoneName: 'shortGeneric' as const },
    { locale: 'en', timeZoneName: 'short' as const }
  ];

  for (const config of localeConfigs) {
    try {
      const formatter = new Intl.DateTimeFormat(config.locale, {
        timeZone: dateTime.zoneName || undefined,
        timeZoneName: config.timeZoneName
      });

      const parts = formatter.formatToParts(jsDate);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      const abbreviation = timeZonePart?.value;

      // Accept if it's a proper abbreviation (not GMT offset format)
      if (
        abbreviation &&
        !abbreviation.includes('GMT') &&
        !abbreviation.includes('UTC') &&
        !abbreviation.includes('+') &&
        !abbreviation.includes('-') &&
        abbreviation.length <= 5 &&
        abbreviation !== dateTime.zoneName?.split('/').pop()?.replace(/_/g, ' ')
      ) {
        return abbreviation;
      }
    } catch (error) {
      continue;
    }
  }

  // Try Luxon's built-in formatters with different tokens
  const luxonFormats = ['ZZZZ', 'ZZZ', 'z'];
  for (const format of luxonFormats) {
    try {
      const zoneName = dateTime.toFormat(format);
      if (
        zoneName &&
        !zoneName.includes('GMT') &&
        !zoneName.includes('UTC') &&
        !zoneName.includes('+') &&
        !zoneName.includes('-') &&
        zoneName !== format
      ) {
        return zoneName;
      }
    } catch (error) {
      continue;
    }
  }

  // Fallback to clean UTC offset
  const offset = Math.floor(dateTime.offset / 60);
  return `UTC${offset >= 0 ? '+' : ''}${offset}`;
};

export const getFormattedYear = (date: string) => {
  // Convert the ISO string to a Luxon DateTime object
  const dateTime = DateTime.fromISO(date, { zone: 'UTC' });
  // Format the date as "Year"
  return dateTime.toFormat('yyyy');
};

export const getFormatedMonthYear = (date: string, includeComma: boolean = true) => {
  // Convert the ISO string to a Luxon DateTime object
  const dateTime = DateTime.fromISO(date, { zone: 'UTC' });
  // Format the date as "Month, Year"
  if (includeComma) return dateTime.toFormat('LLLL, yyyy');
  return dateTime.toFormat('LLLL yyyy');
};

export const getFormattedMonthDayYear = (date: string) => {
  // Convert the ISO string to a Luxon DateTime object
  const dateTime = DateTime.fromISO(date, { zone: 'UTC' });
  // Format the date as "Month Day, Year"
  return dateTime.toFormat('LLLL dd, yyyy');
};

export const getFormatedDateRangeString = (date: string) => {
  // Convert the ISO string to a Luxon DateTime object
  const targetDate = DateTime.fromISO(date, { zone: 'UTC' });
  // Get the current date and time
  const now = DateTime.now();
  // Calculate the difference in years, months, and days
  const diff = targetDate.diff(now, ['years', 'months', 'days']).toObject();

  // Construct a human-readable relative time string
  let years = Math.abs(diff.years || 0);
  const months = Math.abs(diff.months || 0);
  const days = Math.abs(diff.days || 0);
  const label = targetDate > now ? 'away' : 'ago';

  // Increment the years if months are greater than 11
  if (days && months >= 11) years++;

  if (years > 0) {
    return `${years} year${years > 1 ? 's' : ''} ${label}`;
  } else if (months > 0) {
    return `${months} month${months > 1 ? 's' : ''} ${label}`;
  }

  return `${Math.floor(days)} day${Math.floor(days) > 1 ? 's' : ''} ${label}`;
};

export const getDate = (time: string, timeZone: string) => {
  const dateTime = DateTime.fromISO(time, { zone: 'UTC' }).setZone(timeZone);
  return dateTime.toISODate();
};
